import { Plus } from 'lucide-react';
import { authStoreUserTournamentId } from 'modules/auth/store/selectors';
import { MediaManagerTemplate } from 'modules/core/domain/interfaces/supabase/media-manager/entity';
import supabase from 'modules/core/supabase/supabase';
import MediaManagerTemplateList from 'modules/manager/ui/components/media-manager-template-list';
import { Pages } from 'modules/navigation/domain/enums/pages';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import UDButton from 'ud-ui/components/button';
import UDHeaderTitle from 'ud-ui/components/header-title';
import { useMainLayout } from 'ud-ui/layout/main/context';
import useCreateTemplateModal from '../hooks/modals/use-create-template-modal';
import useDeleteTemplateModal from '../hooks/modals/use-delete-template-modal';
import useEditTemplateModal from '../hooks/modals/use-edit-template-modal';

const MediaManagerTemplatesListPage: React.FC = () => {
  const { setHeaderContent } = useMainLayout();
  const navigate = useNavigate();
  const tournamentId = useSelector(authStoreUserTournamentId);

  const [templates, setTemplates] = useState<MediaManagerTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const openTemplate = useCallback(
    (template: MediaManagerTemplate) => {
      navigate(Pages.MEDIA_MANAGER.builders.editor(template.id));
    },
    [navigate],
  );

  useEffect(() => {
    setHeaderContent(<UDHeaderTitle>Медиа менеджер</UDHeaderTitle>);
    return () => setHeaderContent(null);
  }, [setHeaderContent]);

  const loadTemplates = useCallback(() => {
    if (!tournamentId) return;
    setIsLoading(true);
    supabase.mediaManager
      .getTemplates({ tournamentId })
      .then(setTemplates)
      .catch((error) => {
        console.error('Failed to load media manager templates:', error);
      })
      .finally(() => setIsLoading(false));
  }, [tournamentId]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const existingNames = useMemo(() => templates.map((t) => t.name.trim()), [templates]);
  const { modal: createModal, open: openCreateModal } = useCreateTemplateModal({
    tournamentId: tournamentId ?? null,
    existingNames,
    templatesCount: templates.length,
    onCreated: loadTemplates,
  });
  const { modal: editModal, open: openEditModal } = useEditTemplateModal({
    templates,
    onUpdated: loadTemplates,
  });
  const { modal: deleteModal, open: openDeleteModal } = useDeleteTemplateModal({
    onDeleted: loadTemplates,
  });

  return (
    <div className="flex h-full flex-col p-6">
      <div className="mb-4 flex justify-start">
        <UDButton variant="dark" className="flex items-center" onClick={openCreateModal}>
          <Plus size={16} className="mr-2" />
          Добавить шаблон
        </UDButton>
      </div>

      <MediaManagerTemplateList
        templates={templates}
        isLoading={isLoading}
        onCreate={openCreateModal}
        onSelect={openTemplate}
        onEdit={openEditModal}
        onDelete={openDeleteModal}
      />

      {createModal}
      {editModal}
      {deleteModal}
    </div>
  );
};

export default MediaManagerTemplatesListPage;
